import { Box, Button, Divider, Flex, Group, Text } from '@mantine/core';
import {
  IconArrowBackUp,
  IconCheck,
  IconListCheck,
  IconPencil,
  IconPlus,
  IconTrash,
} from '@tabler/icons-react';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { MeetingActionOperationView, MeetingActionProposedAction } from '../chat/types';
import { ProposalDecidedLine } from './ProposalDecidedLine';

type Props = {
  proposal: MeetingActionProposedAction;
  onDecide: (approve: boolean) => void;
  disabled?: boolean;
};

const OPERATION_ICONS = {
  CREATE: IconPlus,
  UPDATE: IconPencil,
  COMPLETE: IconCheck,
  REOPEN: IconArrowBackUp,
  REMOVE: IconTrash,
} as const;

const OPERATION_NOUNS = {
  CREATE: 'new action',
  UPDATE: 'edit',
  COMPLETE: 'mark as complete',
  REOPEN: 'reopen',
  REMOVE: 'removal from the list',
} as const;

const formatDueDate = (dueDate?: string | null): string | null => {
  if (!dueDate) return null;
  return new Date(dueDate).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
};

function OperationBlock({ operation }: { operation: MeetingActionOperationView }) {
  const OperationIcon = OPERATION_ICONS[operation.operation];
  // The action's own wording is authoritative for an existing action; Siesta only supplies
  // new text when it is proposing to change it.
  const actionText = operation.baseDescription ?? operation.description ?? 'This action';
  const newText =
    operation.operation === 'UPDATE' && operation.description ? operation.description : null;
  const due = formatDueDate(operation.dueDate);

  return (
    <Flex direction="column" gap="xxs">
      <Group gap="xs" align="center" wrap="nowrap">
        <OperationIcon size={16} stroke={1.5} color="var(--mantine-color-orange-7)" aria-hidden />
        <Box>
          <Text size="textSm" fw={FONT_WEIGHTS.semibold} c="gray.9">
            {actionText}
          </Text>
          <Text size="textXs" c="gray.7">
            Proposed {OPERATION_NOUNS[operation.operation]}
            {operation.owner ? ` · ${operation.owner}` : ''}
            {due ? ` · due ${due}` : ''}
          </Text>
        </Box>
      </Group>
      {newText && (
        <Text size="textXs" c="gray.8">
          New wording: {newText}
        </Text>
      )}
    </Flex>
  );
}

export function MeetingActionProposalWidget({ proposal, onDecide, disabled }: Props) {
  const isPending = proposal.status === 'PENDING' || !proposal.status;
  const operations = proposal.operations ?? [];
  const operationCount = operations.length;

  return (
    <Box bg="orange.1" p="md" style={{ borderRadius: 'var(--mantine-radius-md)' }}>
      <Flex direction="column" gap="sm">
        <Group gap="xs" align="center" wrap="nowrap">
          <IconListCheck size={16} stroke={1.5} color="var(--mantine-color-orange-7)" aria-hidden />
          <Text size="textSm" fw={FONT_WEIGHTS.semibold} c="gray.9">
            Proposed meeting {operationCount === 1 ? 'action' : `actions (${operationCount})`}
          </Text>
        </Group>
        {proposal.meetingName && (
          <Text size="textXs" c="gray.7">
            {proposal.meetingName}
          </Text>
        )}
        <Text size="textSm" c="gray.8">
          {proposal.summary}
        </Text>
        <Flex direction="column" gap="sm">
          {operations.map((operation, index) => (
            <Flex key={`${operation.actionId ?? 'new'}-${index}`} direction="column" gap="sm">
              {index > 0 && <Divider color="orange.2" />}
              <OperationBlock operation={operation} />
            </Flex>
          ))}
        </Flex>
        {isPending ? (
          <Group gap="sm">
            <Button
              variant="filled"
              color="orange"
              size="sm"
              onClick={() => onDecide(true)}
              disabled={disabled}
            >
              Approve
            </Button>
            <Button variant="default" size="sm" onClick={() => onDecide(false)} disabled={disabled}>
              Deny
            </Button>
          </Group>
        ) : (
          <ProposalDecidedLine status={proposal.status} failureReason={proposal.failureReason} />
        )}
      </Flex>
    </Box>
  );
}
